import { useEffect, useRef, useState } from 'react';
import { api, errMessage } from '../../../../utils/api';
import type {
  DescribeImageResponse,
  ModifyDescriptionResponse,
  ImageRefDTO,
} from '@historyjki/shared';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../ui/Spinner';

interface DescribeStepProps {
  imageRef: ImageRefDTO;
  intent: string;
  /** Called when both describe + modify calls succeed. */
  onComplete: (currentDescription: string, updatedDescription: string) => void;
  onBack: () => void;
}

/**
 * Runs the two Claude calls back-to-back on mount: describe the current
 * image, then modify the resulting JSON to reflect the user's intent. On
 * success the parent advances to the Review step.
 *
 * Guarded with a ref so React 19's StrictMode double-mount in development
 * doesn't trigger two concurrent pipelines (which would both charge the
 * Anthropic quota and race to call onComplete).
 */
export function DescribeStep({
  imageRef,
  intent,
  onComplete,
  onBack,
}: DescribeStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<'describe' | 'modify'>('describe');
  // Tracks the current attempt id; lets us ignore stale promise resolutions
  // (e.g. from React 19's StrictMode double-mount in dev) without ever
  // cancelling the in-flight pipeline before it can call modify-description.
  const attemptIdRef = useRef(0);

  useEffect(() => {
    attemptIdRef.current += 1;
    const myAttempt = attemptIdRef.current;

    (async () => {
      setError(null);
      setPhase('describe');
      try {
        const describeResp = await api.post<DescribeImageResponse>(
          `/projects/${imageRef.project_id}/refine/describe-image`,
          { image_ref: imageRef, intent },
        );
        if (myAttempt !== attemptIdRef.current) return;

        setPhase('modify');
        const modifyResp = await api.post<ModifyDescriptionResponse>(
          `/projects/${imageRef.project_id}/refine/modify-description`,
          {
            current_description: describeResp.description,
            intent,
          },
        );
        if (myAttempt !== attemptIdRef.current) return;

        onComplete(describeResp.description, modifyResp.updated_description);
      } catch (err) {
        if (myAttempt !== attemptIdRef.current) return;
        setError(errMessage(err, 'Failed to analyze the image'));
      }
    })();
    // intent/imageRef are stable per mount; attempt triggers re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const retry = () => {
    setAttempt((n) => n + 1);
  };

  return (
    <div className="refine-step refine-step--describe">
      <h3 className="refine-step__title">Analyzing the current image</h3>
      {error ? (
        <>
          <p className="refine-step__error">{error}</p>
          <div className="refine-step__actions">
            <Button variant="ghost" onClick={onBack}>
              &larr; Back
            </Button>
            <Button onClick={retry}>Retry</Button>
          </div>
        </>
      ) : (
        <div className="refine-step__loading">
          <Spinner size="lg" />
          <p>
            {phase === 'describe'
              ? 'Describing the current image…'
              : 'Applying your requested change…'}
          </p>
        </div>
      )}
    </div>
  );
}
